import { FC, useEffect, useState } from 'react';
import {
  Flex,
  HStack,
  Text,
  Stack,
  Button,
  Tooltip,
  Tag,
  Divider,
  Box,
  Link as ChakraLink,
  useInterval,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spacer
} from '@chakra-ui/react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { IoIosTrophy } from 'react-icons/io';
import { useMemo } from 'react';
import { ExtendedRewardProgram } from 'types';
import { useModal } from 'store/modals';
import { ModalType } from 'components/modals/types';
import { useConnection } from 'store/connection';
import dayjs from 'dayjs';
import RewardProgramCarousel from 'components/rewardPrograms/RewardProgramCarousel';
import GradientBackground from 'components/shared/GradientBackground';
import { RiExternalLinkLine, RiSettings2Line } from 'react-icons/ri';
import { RiCoinsFill } from 'react-icons/ri';
import { FiLink } from 'react-icons/fi';
import { EtherscanLinkType, getEtherscanLink } from 'utils/getEtherscanLink';
import { useWeb3React } from 'hooks/web3';
import { usePrograms } from 'hooks/query/usePrograms';
import { Link } from 'react-router-dom';
import { useHistory, useParams } from 'react-router';
import { useClipboard } from '@chakra-ui/react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { Maintainer } from 'components/rewardPrograms/Maintainer';
import { useRewardProgramState } from 'hooks/query/useRewardProgramState';
import getColorScheme from 'helpers/getColorScheme';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';
import { useConfig } from 'store/config';
import { truncate } from 'utils/address';

type Props = {
  extendedRewardProgram: ExtendedRewardProgram;
  programRewardsUSD: number;
  programStakedUSD: number;
  blockTimestamp: number;
  showExpiredPrograms: boolean;
  showUnverified: boolean;
};

const RewardProgramCard: FC<Props> = ({
  extendedRewardProgram,
  programRewardsUSD,
  programStakedUSD,
  blockTimestamp,
  showExpiredPrograms,
  showUnverified
}) => {
  const { account, library, chainId = 1 } = useWeb3React();
  const history = useHistory();
  const { tried } = useConnection();
  const { openModal } = useModal();
  const { data: extendedPrograms } = usePrograms();
  const [currentTimestamp, setCurrentTimestamp] = useState(blockTimestamp);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isProgramOwner, setIsProgramOwner] = useState(false);
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();
  const toast = useToast();
  const { crucibleFactoryAddress } = useConfig(chainId).config;

  useInterval(() => setCurrentTimestamp(currentTimestamp + 1), 1000);

  const {
    rewardProgram,
    rewardProgramTokens,
    rewardScalingDays,
    rewardMultiplier,
    apy,
    poolAddress
  } = extendedRewardProgram;

  const { data: programStateData } = useRewardProgramState(rewardProgram);

  const getPermalink = () => {
    return `${window.location.origin}/reward-programs/${rewardProgram.address}?network=${chainId}`;
  };

  const { onCopy, hasCopied } = useClipboard(getPermalink());

  const isManagePage = history.location.pathname.includes('/manage');

  useEffect(() => {
    if (hasCopied) {
      toast({
        title: `Copied permalink`,
        status: 'info',
        duration: 2000,
        isClosable: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCopied]);

  useEffect(() => {
    if (account && library) {
      setIsProgramOwner(rewardProgram.owner === account.toLowerCase());
    }
  }, [library, account, rewardProgram]);

  const boxProps = {
    bg: 'gray.700',
    borderRadius: 'xl',
    mt: 4,
    mb: 8
  };

  const stackProps = {
    px: [5, 6],
    py: 4
  };

  const openRewardProgramInfoModal = (defaultTab = 'subscribe') => {
    openModal(ModalType.rewardProgramInfo, {
      rewardProgram,
      defaultTab,
      rewardProgramTokens,
      carouselProps: {
        apy,
        rewardMultiplier,
        rewardScalingTime: rewardScalingDays,
        blockTimestampLoading: !blockTimestamp,
        expiryTimestamp: rewardProgram?.expiryTimestamp,
        daysLeft,
        programRewardsUSD,
        programStakedUSD,
        boxProps,
        stackProps
      }
    });
  };

  const extendedProgram = useMemo(() => {
    if (extendedPrograms) {
      return extendedPrograms.find(
        (extendedProgram) =>
          extendedProgram.rewardProgramAddress === rewardProgram.address
      );
    }
  }, [extendedPrograms, rewardProgram.address]);

  const rewardProgramExpired = useMemo(() => {
    // Reward programs without Crucible factory, Shutdown or Offline state are placed into 'ENDED' state.
    // Also reward programs that have been blacklisted or maintainers that are blacklisted will placed into the 'ENDED' state.
    if (
      extendedProgram &&
      // TODO: Remove vaultFactories object check when live
      extendedProgram.rewardProgram.vaultFactories &&
      (!!!extendedProgram.rewardProgram.vaultFactories.find(
        (vf) =>
          vf.id.split('-')[1].toLowerCase() ===
          crucibleFactoryAddress.toLowerCase()
      ) ||
        extendedProgram.rewardProgram.rewardProgramState ===
          PowerSwitchStatus.SHUTDOWN ||
        extendedProgram.rewardProgram.rewardProgramState ===
          PowerSwitchStatus.OFFLINE ||
        extendedProgram.rewardProgram.isProgramBlocked ||
        extendedProgram.rewardProgram.isMaintainerBlacklisted)
    )
      return true;

    if (extendedProgram) {
      return dayjs
        .unix(extendedProgram.rewardProgram?.expiryTimestamp as number)
        .isBefore(dayjs.unix(currentTimestamp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimestamp, extendedProgram]);

  const daysLeft = useMemo(() => {
    return dayjs
      .unix(rewardProgram?.expiryTimestamp as number)
      .isBefore(dayjs.unix(currentTimestamp))
      ? 'Ended'
      : dayjs
          .unix(rewardProgram?.expiryTimestamp as number)
          .diff(dayjs.unix(currentTimestamp), 'days') + 'd left';
  }, [rewardProgram?.expiryTimestamp, currentTimestamp]);

  const pendingLaunch = useMemo(() => {
    return !!(
      rewardProgram.startTime && rewardProgram.startTime > currentTimestamp
    );
  }, [currentTimestamp, rewardProgram]);

  useEffect(() => {
    if (
      rewardProgramAddress &&
      rewardProgramAddress.toLowerCase() === rewardProgram.address
    ) {
      if (rewardProgramExpired) {
        toast({
          title: 'This reward program has ended',
          status: 'warning',
          duration: 4000,
          isClosable: true
        });
      } else if (pendingLaunch) {
        toast({
          title: "This reward program hasn't started yet",
          status: 'warning',
          duration: 4000,
          isClosable: true
        });
      } else {
        openRewardProgramInfoModal('information');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardProgramAddress]);

  const getCountdown = () => {
    const remaining = rewardProgram.startTime! - currentTimestamp;
    const seconds = Math.floor(remaining % 60);
    const minutes = Math.floor((remaining / 60) % 60);
    const hours = Math.floor((remaining / (60 * 60)) % 24);
    const days = Math.floor(remaining / (60 * 60 * 24));
    return `Starts in: 
    ${days ? days + 'd ' : ''}
    ${hours || days ? hours + 'h ' : ''}
    ${minutes || hours ? minutes + 'm ' : ''}
    ${!hours && (seconds || minutes) ? seconds + 's ' : ''}`;
  };

  const renderLabel = () => {
    if (isManagePage) {
      return 'Manage';
    } else if (pendingLaunch && !rewardProgramExpired) {
      return getCountdown();
    } else if (rewardProgramExpired) {
      return 'Entry Closed';
    } else {
      return 'Subscribe';
    }
  };

  return (
    <GradientBackground
      display={
        (rewardProgram.maintainer === 'Unverified' && !showUnverified) ||
        (rewardProgramExpired && !showExpiredPrograms)
          ? 'none'
          : 'block'
      }
      visible={rewardProgram.featured}
      borderRadius='2xl'
      height='100%'
    >
      <Flex
        flexDir='column'
        p={['24px', '28px']}
        borderRadius='2xl'
        bg='gray.800'
        height='100%'
        position='relative'
      >
        {rewardProgram.featured && !isManagePage && (
          <Text
            bgColor='purple.400'
            borderRadius='12px'
            fontSize='sm'
            px='12px'
            py='5px'
            position='absolute'
            top='-16px'
            left='16px'
          >
            Featured
          </Text>
        )}
        <HStack justifyContent='space-between' maxW={'100%'}>
          <Text
            fontSize={['19px', null, '23px', '23px', '23px']}
            fontWeight='500'
            isTruncated
          >
            {rewardProgram.name}
          </Text>
          <Spacer />
          <Tooltip hasArrow placement='bottom' label='View Leaderboard'>
            <IconButton
              bg='whiteAlpha.50'
              variant='ghost'
              aria-label='leaderboard'
              onClick={() =>
                history.push(`/leaderboard/${rewardProgram.address}`)
              }
              icon={<IoIosTrophy cursor='pointer' />}
            />
          </Tooltip>
          <Menu autoSelect={false} placement='bottom-end'>
            <MenuButton
              as={IconButton}
              icon={<BiDotsVerticalRounded size={25} />}
              variant='outline'
            />
            <MenuList zIndex={999}>
              <ChakraLink
                href={getEtherscanLink(
                  chainId,
                  poolAddress,
                  EtherscanLinkType.ADDRESS
                )}
                isExternal
              >
                <MenuItem icon={<RiCoinsFill />}>Reward Pool</MenuItem>
              </ChakraLink>
              <ChakraLink
                href={getEtherscanLink(
                  chainId,
                  rewardProgram.address,
                  EtherscanLinkType.ADDRESS
                )}
                isExternal
              >
                <MenuItem icon={<RiExternalLinkLine />}>
                  Program Contract
                </MenuItem>
              </ChakraLink>
              <MenuItem
                onClick={() => onCopy()}
                icon={<FiLink cursor='pointer' />}
              >
                Copy Permalink
              </MenuItem>
              {isProgramOwner && !isManagePage && (
                <ChakraLink as={Link} to={`/manage/${rewardProgram.address}`}>
                  <MenuItem icon={<RiSettings2Line />}>Manage</MenuItem>
                </ChakraLink>
              )}
            </MenuList>
          </Menu>
        </HStack>
        <HStack flexGrow={1} alignItems='center'>
          <Text color='gray.300' fontSize='sm'>
            {isManagePage && (programStateData || programStateData === 0) ? (
              <Tag colorScheme={getColorScheme(programStateData)}>
                {mapPowerSwitchStatusToDescription[programStateData]}
              </Tag>
            ) : (
              <Maintainer
                maintainer={rewardProgram.maintainer}
                owner={rewardProgram.owner}
              />
            )}{' '}
            | {truncate(rewardProgramTokens?.stakingToken?.tokenSymbol, 15, 0)}
          </Text>
          <Tooltip
            hasArrow
            placement='bottom'
            bg='gray.600'
            borderRadius='xl'
            py={4}
            px={6}
            boxShadow='dark-lg'
            maxWidth='380'
            overflow={'auto'}
            isOpen={isTooltipOpen}
            label={
              <Flex justifyContent='space-between'>
                <Stack mr={4}>
                  <Text color='white' fontSize='md'>
                    Staking token
                  </Text>
                  <Tag color='white' width='fit-content'>
                    {truncate(
                      rewardProgramTokens?.stakingToken?.tokenSymbol,
                      12,
                      0
                    )}
                  </Tag>
                </Stack>
                <div>
                  <Divider orientation='vertical' mr={4} />
                </div>
                <Stack>
                  <Text color='white' fontSize='md'>
                    Rewards
                  </Text>
                  <HStack wrap='wrap' spacing='none'>
                    <Tag color='white'>
                      {truncate(
                        rewardProgramTokens?.rewardToken?.tokenSymbol,
                        12,
                        0
                      )}
                    </Tag>
                    {rewardProgramTokens?.bonusTokens?.map((token: any) => (
                      <>
                        <Tag color='white' key={token.address}>
                          {truncate(token.tokenSymbol, 12, 0)}
                        </Tag>
                      </>
                    ))}
                  </HStack>
                </Stack>
              </Flex>
            }
          >
            <Box color='gray.300'>
              <AiOutlineInfoCircle
                size='18px'
                color='gray.300'
                onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                onMouseEnter={() => setIsTooltipOpen(true)}
                onMouseLeave={() => setIsTooltipOpen(false)}
              />
            </Box>
          </Tooltip>
        </HStack>

        <RewardProgramCarousel
          showMaintainer={false}
          showDivider={false}
          maintainer={rewardProgram.maintainer}
          owner={rewardProgram.owner}
          apy={apy}
          rewardMultiplier={rewardMultiplier}
          rewardScalingTime={rewardScalingDays}
          expiryTimestamp={rewardProgram?.expiryTimestamp}
          daysLeft={daysLeft || '-'}
          programRewardsUSD={programRewardsUSD}
          programStakedUSD={programStakedUSD}
          boxProps={boxProps}
          stackProps={stackProps}
        />
        <Button
          isFullWidth
          onClick={
            isManagePage
              ? () => history.replace(`/manage/${rewardProgram.address}`)
              : () => openRewardProgramInfoModal()
          }
          isDisabled={
            !isManagePage &&
            (rewardProgramExpired || pendingLaunch || !extendedProgram)
          }
          isLoading={!tried}
        >
          {renderLabel()}
        </Button>
      </Flex>
    </GradientBackground>
  );
};

export default RewardProgramCard;
